<template>
    <div class="flex flex-col items-center justify-center">
        <UAuthForm
            :schema="schema"
            :state="state"
            icon="i-lucide-user-circle"
            :fields="fields"
            :loading="inProgress"
            :disabled="inProgress"
            title="Sign In"
            description="Access your account."
            :ui="{
                form: 'space-y-5 ',
                header: 'mb-6 mt-6 ',
                leading: 'mb-0 pb-2',
                footer: 'mt-5',
                description: 'text-muted font-normal not-italic',
                leadingIcon: 'size-6.5 text-(--ui-text-primary-highlighted)',
                title: 'text-(--ui-text-primary-highlighted)',
                password: 'mb-1',
            }"
            @submit="login"
        >
            <template #validation>
                <CardBlock v-if="authErrorConfig" :config="authErrorConfig"></CardBlock>
            </template>
            <template #submit>
                <div class="space-y-4">
                    <!-- Forgot Password Link -->
                    <div class="text-md text-left mb-6">
                        <SharedLinkButton
                            :disabled="inProgress"
                            :class="linkButtonClass"
                            @click.prevent="openForgotPassword"
                        >
                            Forgot Password?
                        </SharedLinkButton>
                    </div>

                    <!-- Submit Button -->
                    <UButton
                        type="submit"
                        block
                        :loading="inProgress"
                        :disabled="inProgress"
                        :class="submitButtonClass"
                    >
                        Continue
                    </UButton>
                </div>
            </template>

            <template #footer>
                <SharedLinkButton
                    v-if="showCreateAccountLink"
                    :disabled="inProgress"
                    :class="linkButtonClass"
                    @click.prevent="openSignUp"
                >
                    Create Account
                </SharedLinkButton>
                <SharedLinkButton v-else :class="linkButtonClass" @click.prevent="goHome">
                    Go to Home
                </SharedLinkButton>
            </template>
        </UAuthForm>
    </div>
</template>

<script setup lang="ts">
import * as z from 'zod'
import CardBlock from '~~/layers/base/components/shared/CardBlock.vue'
import { getAuthErrorConfig } from '~~/layers/base/data/auth'
import type { Login, AuthFormField } from '~~/layers/base/types/auth'

const props = withDefaults(
    defineProps<{
        /** When true, hide the "Create Account" link in the auth form footer. */
        hideCreateAccount?: boolean
    }>(),
    {
        hideCreateAccount: false,
    }
)

const runtimeConfig = useRuntimeConfig() as {
    public?: {
        storefrontUrl?: string
    }
}
const storefrontUrl = runtimeConfig.public?.storefrontUrl
// 1. COMPOSABLES & STORES
const { loginAndAssignDesignProject } = useAuthActions()

const customerAuthStore = useCustomerAuthAccsStore()
// 2. EMITS
const emit = defineEmits(['login-success', 'login-failed', 'open-sign-up', 'open-forgot-password'])

const { inProgress, errorMessage } = storeToRefs(customerAuthStore)

const commonInputClass =
    'placeholder:opacity-100 h-15 placeholder:italic placeholder:text-(--ui-text-primary-dimmed) text-(--ui-text-primary) ring-2 ring-(--ui-border-primary-elevated)'

const linkButtonClass = 'text-(--ui-text-primary) font-medium underline cursor-pointer'

const submitButtonClass =
    'cursor-pointer h-15 bg-(--color-action-800) hover:bg-(--color-action-900) active:bg-(--color-action-700) disabled:bg-(--color-neutral-800) rounded font-normal'
// 4. STATE
const state = reactive<Login>({
    email: '',
    password: '',
})

// 5. VALIDATION SCHEMA
const schema = z.object({
    email: z
        .string({ message: 'Email is required' })
        .min(1, 'Email is required')
        .email('Invalid email'), // Email validation
    password: z.string({ message: 'Password is required' }).min(1, 'Password is required'),
})

// 6. FORM FIELDS
const fields: AuthFormField[] = [
    {
        name: 'email',
        label: 'Email',
        type: 'text',
        placeholder: 'Enter your email',
        ui: {
            base: commonInputClass,
        },
    },
    {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: 'Enter your password',
        class: 'password-field',
        ui: {
            base: commonInputClass,
        },
    },
] as const

onBeforeUnmount(() => {
    errorMessage.value = null
})

const login = async (payload: { data: Login }): Promise<void> => {
    try {
        authMessage.value = null
        await loginAndAssignDesignProject(payload.data)
        emit('login-success')
    } catch (error: unknown) {
        emit('login-failed', error)
    }
}

const openSignUp = (): void => {
    emit('open-sign-up', 'signup')
}
const openForgotPassword = (): void => {
    emit('open-forgot-password', 'forgot-password')
}

/**
 * CardBlock config for authentication errors
 */
const { authMessage, isForcedAuth } = useAuthFlow()
/** Admin login and other restricted contexts can suppress self-serve signup entry points. */
const showCreateAccountLink = computed(() => !isForcedAuth.value && !props.hideCreateAccount)
const authErrorConfig = computed(() => {
    const message = errorMessage.value || authMessage.value
    if (!message) return undefined
    return getAuthErrorConfig(message)
})
const goHome = (): void => {
    customerAuthStore.logout()
    window.location.href = `${storefrontUrl}`
}
</script>

<style scoped>
:deep(input[type='password']::placeholder) {
    color: var(--ui-text-primary-dimmed) !important;
}
:deep(label) {
    color: var(--ui-text-primary) !important;
    font-size: 0.875rem !important;
    font-style: normal !important;
    margin-bottom: 4px;
}
:deep(.password-field button) {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
    width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}

:deep(.password-field input) {
    height: 3.75rem !important;
    box-shadow: inset 0 0 0 2px var(--ui-border-primary-elevated) !important;
}

:deep(.password-field input:focus-visible:not(.ring-error)) {
    box-shadow: inset 0 0 0 2px var(--ui-border-primary) !important;
}
:deep(.password-field input.ring-error:focus-visible) {
    box-shadow: inset 0 0 0 2px var(--ui-error) !important;
}
</style>
